import { memo } from 'react';

import type { IGCommentWatcherClassContent } from '../../../../bridge/types/artifact.ts';

import { CommentWatcherModeRow } from './comment-watcher/CommentWatcherModeRow.tsx';
import { TimelineStage } from './comment-watcher/TimelineStage.tsx';
import { CommentMonitorCondition } from './CommentMonitorCondition';
import { CommentReply } from './CommentReply';
import type { CommentWatcherMode } from './commentWatcherUtils';
import { DmReply } from './DmReply';

interface CommentWatcherProps {
	content: IGCommentWatcherClassContent;
	mode: CommentWatcherMode;
	isReadOnly?: boolean;
	// When true, the automated/review-first switch is editable (pre-activation).
	// Otherwise a static mode label is shown. Gated on an explicit prop, not
	// isReadOnly, because the running-monitor panel renders with isReadOnly=false.
	modeEditable?: boolean;
	onContentChange?: (updates: Record<string, unknown>) => void;
}

export const CommentWatcher = memo(function CommentWatcher({
	content,
	mode,
	isReadOnly = false,
	modeEditable = false,
	onContentChange,
}: CommentWatcherProps) {
	const showReply = mode === 'reply-only' || mode === 'reply-and-dm';
	const showDm = mode === 'dm-only' || mode === 'reply-and-dm';

	// Build ordered list of visible stages for step numbering + isLast
	const stages = [
		{ key: 'detect', show: true },
		{ key: 'reply', show: showReply },
		{ key: 'dm', show: showDm },
	].filter((s) => s.show);

	const stepFor = (key: string) => stages.findIndex((s) => s.key === key) + 1;
	const isLastFor = (key: string) => stages[stages.length - 1].key === key;

	// The automated/review-first choice only governs reply/DM publishing;
	// alert-only watchers never publish, so the mode row is irrelevant there.
	const showModeRow = showReply || showDm;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', paddingTop: 16 }}>
			{showModeRow && (
				<CommentWatcherModeRow
					content={content}
					editable={modeEditable}
					onContentChange={onContentChange}
				/>
			)}
			<TimelineStage
				stepNumber={stepFor('detect')}
				stepLabel="Detect"
				isLast={isLastFor('detect')}
			>
				<CommentMonitorCondition
					content={content}
					isReadOnly={isReadOnly}
					onContentChange={onContentChange}
				/>
			</TimelineStage>

			{showReply && (
				<TimelineStage
					stepNumber={stepFor('reply')}
					stepLabel="Reply publicly"
					isLast={isLastFor('reply')}
				>
					<CommentReply
						content={content}
						isReadOnly={isReadOnly}
						onContentChange={onContentChange}
					/>
				</TimelineStage>
			)}

			{showDm && (
				<TimelineStage
					stepNumber={stepFor('dm')}
					stepLabel="Send DM"
					isLast={isLastFor('dm')}
				>
					<DmReply
						content={content}
						isReadOnly={isReadOnly}
						onContentChange={onContentChange}
					/>
				</TimelineStage>
			)}
		</div>
	);
});
